import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Formik, useField } from 'formik';
import cn from 'classnames';

import CityAutoCompleteSelectRaw from 'components/InputsField/formikInputs/CityAutoCompleteSelect';

const CityAutoCompleteSelect = CityAutoCompleteSelectRaw as React.ComponentType<any>;
import AuthMessage from 'components/PublicTableElements/AuthMessage';
import { SET_MAIN_PAGE_SEARCH } from 'store/mainPageSearch';
import { FREIGHTS, TRANSPORT } from 'helpers/routes';
import { DEFAULT_ADDRESS_RADIUS } from '@/constants/settings';

import styles from './styles.module.scss';

const addressSelectStyles = {
  control: (provided: any) => ({
    ...provided,
    border: 'none',
    boxShadow: 'none',
    minHeight: 'auto',
    background: 'transparent',
    padding: 0,
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: 0,
  }),
  input: (provided: any) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  singleValue: (provided: any) => ({
    ...provided,
    margin: 0,
    fontFamily: 'Arial, sans-serif',
    fontSize: 16,
    lineHeight: '24px',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    margin: 0,
    fontFamily: 'Arial, sans-serif',
    fontSize: 16,
    lineHeight: '24px',
    color: '#959baa',
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
    left: -14,
    right: -14,
    width: 'auto',
    marginTop: 12,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    zIndex: 9999,
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: 0,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    padding: '12px 16px 12px 40px',
    fontFamily: 'Arial, sans-serif',
    fontSize: 16,
    lineHeight: '24px',
    color: '#0d0d0d',
    backgroundColor: state.isFocused ? '#f3f5fa' : '#ffffff',
    cursor: 'pointer',
  }),
};

const locationPinIcon = new URL('../../../../img/main-page/location-pin.svg', import.meta.url).href;
const switchArrowsIcon = new URL('../../../../img/main-page/switch-arrows.svg', import.meta.url).href;
const magnifyingGlassIcon = new URL('../../../../img/main-page/magnifying-glass.svg', import.meta.url).href;

enum SearchTab {
  FindCargo = 'findCargo',
  FindTransport = 'findTransport',
  AddCargo = 'addCargo',
  AddTransport = 'addTransport',
}

interface RadiusFieldProps {
  name: string;
  placeholder?: string;
}

const RadiusField: React.FC<RadiusFieldProps> = ({ name, placeholder }) => {
  const [field, , helpers] = useField(name);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '');
    helpers.setValue(val ? Number(val) : '');
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={field.value ?? ''}
      onChange={handleChange}
      placeholder={placeholder}
      className={styles.plainInput}
    />
  );
};

const HeroSearch: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(SearchTab.FindCargo);
  const [showAuthMessage, setShowAuthMessage] = useState(false);
  const user = useSelector((state: any) => state.profile.profileData);
  const isAuthenticated = !!user?.id;

  const tabs = [
    { key: SearchTab.FindCargo, label: t('main.hero.findCargo') },
    { key: SearchTab.FindTransport, label: t('main.hero.findTransport') },
    { key: SearchTab.AddCargo, label: t('main.hero.addCargo') },
    { key: SearchTab.AddTransport, label: t('main.hero.addTransport') },
  ];

  const initialValues = {
    loading_address: null,
    loading_address_radius: DEFAULT_ADDRESS_RADIUS,
    unloading_address: null,
    unloading_address_radius: DEFAULT_ADDRESS_RADIUS,
  };

  const handleTabClick = useCallback((tab: SearchTab) => {
    if (tab === SearchTab.AddCargo) {
      if (!isAuthenticated) {
        setShowAuthMessage(true);
        return;
      }
      navigate(FREIGHTS.add);
      return;
    }
    if (tab === SearchTab.AddTransport) {
      if (!isAuthenticated) {
        setShowAuthMessage(true);
        return;
      }
      navigate(TRANSPORT.addToExchange);
      return;
    }
    setActiveTab(tab);
  }, [isAuthenticated, navigate]);

  const handleSubmit = useCallback((values: typeof initialValues) => {
    dispatch({
      type: SET_MAIN_PAGE_SEARCH,
      payload: {
        loading_address: values.loading_address,
        loading_address_radius: values.loading_address_radius,
        unloading_address: values.unloading_address,
        unloading_address_radius: values.unloading_address_radius,
      },
    });
    navigate(activeTab === SearchTab.FindTransport ? TRANSPORT.search : FREIGHTS.search);
  }, [dispatch, navigate, activeTab]);

  const handleSwitch = useCallback((setFieldValue: any, values: any) => {
    const tempLoading = values.loading_address;
    const tempLoadingRadius = values.loading_address_radius;
    setFieldValue('loading_address', values.unloading_address);
    setFieldValue('loading_address_radius', values.unloading_address_radius);
    setFieldValue('unloading_address', tempLoading);
    setFieldValue('unloading_address_radius', tempLoadingRadius);
  }, []);

  return (
    <section className={styles.hero}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          {t('main.hero.title')}
        </h1>

        <div className={styles.formSection}>
          <div className={styles.tabsWrapper}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={cn(styles.tab, {
                  [styles.tabActive]: activeTab === tab.key &&
                    (tab.key === SearchTab.FindCargo || tab.key === SearchTab.FindTransport),
                })}
                onClick={() => handleTabClick(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <AuthMessage open={showAuthMessage} onClose={() => setShowAuthMessage(false)} />

          <Formik initialValues={initialValues} onSubmit={handleSubmit}>
            {({ setFieldValue, values }) => (
              <Form className={styles.form}>
                <div className={styles.formContent}>
                  <div className={styles.inputRow}>
                    <div className={styles.addressInput}>
                      <label className={styles.inputLabel}>{t('main.hero.from')}</label>
                      <CityAutoCompleteSelect
                        name="loading_address"
                        placeholder={t('main.hero.addressPlaceholder')}
                        className={styles.selectInput}
                        styles={addressSelectStyles}
                        icon={locationPinIcon}
                        useMapboxSearch
                        isClearable
                      />
                    </div>
                    <div className={styles.radiusInput}>
                      <label className={styles.inputLabel}>{t('main.hero.radius')}</label>
                      <RadiusField name="loading_address_radius" placeholder="1000" />
                    </div>
                  </div>

                  <button
                    type="button"
                    className={styles.switchButton}
                    onClick={() => handleSwitch(setFieldValue, values)}
                    title={t('main.hero.switchDirection')}
                  >
                    <img src={switchArrowsIcon} alt="Switch" />
                  </button>

                  <div className={styles.inputRow}>
                    <div className={styles.addressInput}>
                      <label className={styles.inputLabel}>{t('main.hero.to')}</label>
                      <CityAutoCompleteSelect
                        name="unloading_address"
                        placeholder={t('main.hero.addressPlaceholder')}
                        className={styles.selectInput}
                        styles={addressSelectStyles}
                        icon={locationPinIcon}
                        useMapboxSearch
                        isClearable
                      />
                    </div>
                    <div className={styles.radiusInput}>
                      <label className={styles.inputLabel}>{t('main.hero.radius')}</label>
                      <RadiusField name="unloading_address_radius" placeholder="1000" />
                    </div>
                  </div>

                  <button type="submit" className={styles.searchButton}>
                    <img src={magnifyingGlassIcon} alt={t('main.hero.searchButton')} />
                  </button>
                </div>

                <button type="submit" className={styles.searchButtonMobile}>
                  {t('main.hero.searchButton')}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default HeroSearch;
